import {
  Collapsible,
  PageHeader,
  SimpleErrorNotification,
  Sortable,
  UploadMediaModal,
} from "shared/components";
import { useMedia } from "./useMedia";
import { Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { MediaImage, Upload, WarningTriangle, Xmark } from "iconoir-react";
import { ImageMedia } from "shared/components";
import { AssetImage, EnumEntityReferenceType } from "gql/generated";
import { Button, ButtonProps, Icon, Text } from "@ttc3k/trekker";
import { useState } from "react";

export type MediaProps = {
  images: Partial<AssetImage>[];
  setSaveButtonProps: (saveButtonProps: ButtonProps) => void;
  setPrimaryButtonProps?: (primaryButtonProps: ButtonProps) => void;
  entityType: EnumEntityReferenceType;
  _entityId: string;
  disableFeaturedImageTag?: boolean;
};

export const Media = ({
  images,
  setSaveButtonProps,
  setPrimaryButtonProps,
  _entityId,
  entityType,
  disableFeaturedImageTag,
}: MediaProps) => {
  const { t } = useTranslation();
  const {
    imagesToKeep,
    isUploadModalOpen,
    isPoorQualityImageMessageVisible,
    handleUploadClick,
    handleUploadComplete,
    handleImageDeleted,
    handleCaptionUpdated,
    handleSortableChange,
    setIsPoorQualityImageMessageVisible,
  } = useMedia({
    images,
    setSaveButtonProps,
    setPrimaryButtonProps,
    _entityId,
    entityType,
  });

  const [isSortableDisabled, setIsSortableDisabled] = useState(false);

  if (!imagesToKeep) {
    return <SimpleErrorNotification />;
  }

  return (
    <>
      <Flex flexDir={"column"} gap={"400"}>
        <PageHeader
          title={t("core:MEDIA.TITLE")}
          description={t("core:MEDIA.DESCRIPTION")}
          Icon={MediaImage}
          ctaButtonCopy={t("core:UPLOAD")}
          ctaButtonProps={{
            Icon: Upload,
            onClick: handleUploadClick,
          }}
        />
        <Collapsible open={isPoorQualityImageMessageVisible}>
          <Flex
            flexDir="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap="200"
            backgroundColor="bg.warning.lightest"
            borderStyle="solid"
            borderColor="border.warning"
            borderWidth="2px"
            py="300"
            px="400"
            rounded="150"
            position="relative"
          >
            <Button
              visual="unstyled"
              position="absolute"
              top="12px"
              right="12px"
              onClick={() => setIsPoorQualityImageMessageVisible(false)}
            >
              <Icon
                Element={Xmark}
                size="md"
                color="icon.warning.light"
                _hover={{ color: "icon.warning.mid" }}
              />
            </Button>

            <Flex flexDir="column" gap="50">
              <Flex flexDir="row" alignItems="center" gap="100">
                <Icon
                  Element={WarningTriangle}
                  size="md"
                  color="icon.warning.dark"
                />
                <Text visual="bodySemiBold" color="text.warning.dark">
                  {t("core:IMAGE.LOW_QUALITY_DETECTED")}
                </Text>
              </Flex>

              <Text color="text.warning.dark">
                {t("core:IMAGE.LOW_QUALITY_FLAGGED")}
              </Text>
            </Flex>

            <Button
              visual="warning"
              size="sm"
              onClick={() => setIsPoorQualityImageMessageVisible(false)}
            >
              {t("core:GOT_IT")}
            </Button>
          </Flex>
        </Collapsible>
        <Sortable
          disabled={isSortableDisabled}
          value={imagesToKeep}
          selectorID="_id"
          sortableComponent={(image: Partial<AssetImage>, i: number) => (
            <ImageMedia
              image={image}
              onDelete={handleImageDeleted}
              onCaptionUpdated={(image) =>
                handleCaptionUpdated(image as Partial<AssetImage>)
              }
              showTag={disableFeaturedImageTag ? false : i === 0}
              onModalToggle={setIsSortableDisabled}
            />
          )}
          onChange={handleSortableChange}
        />
      </Flex>
      <UploadMediaModal
        isOpen={isUploadModalOpen}
        isPublicWidget={false}
        onClose={handleUploadClick}
        onUploadComplete={handleUploadComplete}
      />
    </>
  );
};
